import { ApiError, authHeaders, requestJson } from './http';
import { ComponentReview } from '../audio/personalMessagesApi';
import { BedtimePair } from '../context/bedtime/bedtimeContextModel';

const base = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';
const root = `${base}/household-stories`;
export interface HouseholdStory { narration_approved?: boolean; funding_ready?: boolean; source_kind?: 'legacy'; source_render_id?: string; binding_id: string; candidate_id: string | null; story_id: string; title: string }
export type Messages = { intro: ComponentReview | null; goodnight: ComponentReview | null };
export interface Catalog { funding_label?: string | null; component_review_policy: string; stories: HouseholdStory[]; messages: Messages }
export interface SharedStatus { component_review_policy: string; funding_ready?: boolean; candidate_id: string; job_id: string | null; status: 'queued' | 'running' | 'held' | 'ready'; narration_approved: boolean; messages: Messages; audio_sha256: string | null; manifest_sha256: string | null }
export interface Playback { component_review_policy: string; composition_id: string; audio_sha256: string; bedtime_consumed: boolean; audio_url: string }
function policy<T extends {component_review_policy: string}>(value: T): T {
  if (value.component_review_policy !== 'component-audio-v1') throw new Error('Story review is unavailable');
  return value;
}
export function approvedMessages(messages: Messages | undefined): boolean {
  return Boolean(messages && (['intro', 'goodnight'] as const).every((kind) => {
    const receipt = messages[kind];
    return receipt?.supported === true && receipt.kind === kind && receipt.status === 'approved'
      && /^[0-9a-f-]{36}$/.test(receipt.recording_version_id) && /^[0-9a-f]{64}$/.test(receipt.audio_sha256);
  }));
}
export async function sharedCatalog(pair: BedtimePair, signal?: AbortSignal): Promise<Catalog> {
  return policy(await requestJson<Catalog>(`${root}/catalog?voice_id=${encodeURIComponent(pair.voiceId)}&listener_id=${pair.listenerId}`, {headers: authHeaders(), cache: 'no-store', signal}));
}
export async function sharedStatus(candidate: string, listener: string, signal?: AbortSignal, source?: 'legacy'): Promise<SharedStatus> {
  return policy(await requestJson<SharedStatus>(`${root}${source === 'legacy' ? '/legacy' : ''}/${candidate}/status?listener_id=${listener}`, {headers: authHeaders(), cache: 'no-store', signal}));
}
async function post<T>(path: string, body: object, signal?: AbortSignal, timeoutMs?: number): Promise<T> {
  return requestJson<T>(`${root}${path}`, {method: 'POST', headers: {...authHeaders(), 'Content-Type': 'application/json'}, body: JSON.stringify(body), signal}, timeoutMs);
}
export async function prepareShared(pair: BedtimePair, binding: string, request: string, signal?: AbortSignal) {
  return policy(await post<{candidate_id: string; status: string; component_review_policy: string}>('/prepare', {voice_id: pair.voiceId, listener_id: pair.listenerId, binding_id: binding, request_id: request}, signal));
}
export async function retryShared(candidate: string, request: string, signal?: AbortSignal) {
  return policy(await post<{status: string; component_review_policy: string}>(`/${candidate}/retry`, {request_id: request}, signal));
}
export async function approveShared(candidate: string, status: SharedStatus, request: string, signal?: AbortSignal, source?: 'legacy') {
  const receipt = policy(await post<{status: string; audio_sha256: string; component_review_policy: string}>(`${source === 'legacy' ? '/legacy' : ''}/${candidate}/review`, {audio_sha256: status.audio_sha256, manifest_sha256: status.manifest_sha256, request_id: request, reviewed: true}, signal));
  if (receipt.status !== 'approved' || receipt.audio_sha256 !== status.audio_sha256) throw new Error('Story approval was not saved');
}
export async function preparePlayback(candidate: string, listener: string, signal?: AbortSignal, source?: 'legacy'): Promise<Playback> {
  // Verify and assemble exact stored components before the single atomic claim.
  // Slow first assembly has the same bounded allowance as saved-source import.
  return policy(await post<Playback>(`${source === 'legacy' ? '/legacy' : ''}/${candidate}/listen`, {listener_id: listener}, signal, 120_000));
}
export async function exactSharedAudio(path: string, expectedHash: string, signal?: AbortSignal): Promise<Blob> {
  if (!/^[0-9a-f]{64}$/.test(expectedHash) || !/^\/(?:(?:legacy\/)?[0-9a-f-]{36}\/narration|(?:legacy\/)?audio\/[0-9a-f-]{36})$/.test(path)) throw new Error('Story audio is unavailable');
  const response = await fetch(`${root}${path}`, {headers: authHeaders(), cache: 'no-store', signal});
  if (!response.ok) throw new Error('Story audio is unavailable');
  const data = await response.arrayBuffer();
  const digest = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', data)), (b) => b.toString(16).padStart(2, '0')).join('');
  if (digest !== expectedHash) throw new Error('Story audio changed');
  return new Blob([data], {type: 'audio/wav'});
}

export async function importSavedNarration(render: string, signal?: AbortSignal) {
  // Exact historical audio verification can outlast an ordinary JSON request.
  // Keep navigation cancellation and a finite deadline without restarting work.
  return policy(await post<{candidate_id: string; component_review_policy: string}>('/legacy/import', {render_id: render}, signal, 120_000));
}

interface PreviewTransport {
  component_review_policy: string; audio_sha256: string; manifest_sha256: string;
  preview_sha256: string; audio_base64: string; source_frames: number;
  sample_rate: number; preview_frames: number; source_start_frames: number[];
}
export async function exactSharedPreviews(candidate: string, status: SharedStatus, signal?: AbortSignal, source?: 'legacy'): Promise<Blob> {
  if (!/^[0-9a-f-]{36}$/.test(candidate) || !status.audio_sha256 || !status.manifest_sha256) throw new Error('Story previews unavailable');
  // Keep cancellation attached through the response body on slow connections.
  const requestController = new AbortController();
  const abort = () => requestController.abort();
  signal?.addEventListener('abort', abort, {once: true});
  if (signal?.aborted) abort();
  let timedOut = false;
  const timer = window.setTimeout(() => { timedOut = true; abort(); }, 120_000);
  let result: PreviewTransport;
  try {
    const response = await fetch(`${root}${source === 'legacy' ? '/legacy' : ''}/${candidate}/previews`, {headers: authHeaders(), cache: 'no-store', signal: requestController.signal});
    if (!response.ok) throw new ApiError(response.status === 401 || response.status === 403 ? 'unauthorized' : response.status >= 500 ? 'service_unavailable' : 'conflict');
    result = policy(await response.json() as PreviewTransport);
  } catch (error) {
    if (timedOut) throw new ApiError('timeout');
    throw error;
  } finally {
    window.clearTimeout(timer); signal?.removeEventListener('abort', abort);
  }
  const { source_frames: frames, sample_rate: rate, preview_frames: width } = result;
  if (result.audio_sha256 !== status.audio_sha256 || result.manifest_sha256 !== status.manifest_sha256
      || !Number.isSafeInteger(frames) || frames < 3 || !Number.isSafeInteger(rate) || rate <= 0
      || width !== Math.min(25 * rate, Math.floor(frames / 3))
      || JSON.stringify(result.source_start_frames) !== JSON.stringify([0, Math.floor((frames - width) / 2), frames - width])) throw new Error('Story previews changed');
  const data = Uint8Array.from(atob(result.audio_base64), c => c.charCodeAt(0));
  const digest = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', data)), b => b.toString(16).padStart(2, '0')).join('');
  if (digest !== result.preview_sha256) throw new Error('Story previews changed');
  return new Blob([data], {type: 'audio/wav'});
}
